/**
 * Generate now — reads the LIVE preview into the confirm dialog (same
 * numbers as the Day timeline), creates DRAFTs only. Disabled states say
 * exactly why: permission, kill switch, disabled plan, nothing plannable.
 * Re-clicking re-syncs the same day (the reconcile is idempotent).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { Zap } from 'lucide-react';
import type { DayPlanPreview } from '@trafficpulse/shared';

import { Button, ConfirmDialog, Tooltip } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';

import type { DropGenPlanDetail } from '../../drop-generator.types';
import { useDropGeneratorMutations } from '../../useDropGeneratorMutations';
import { anyGroupAutoSends } from '../../utils/plan-status';
import {
  GENERATE_CONFIRM_LABEL,
  GENERATE_CONFIRM_TITLE,
  GENERATE_DRAFTS_ONLY,
  GENERATE_FILL_LABEL,
  GENERATE_FILLING_NOTE,
  GENERATE_LABEL,
  GENERATE_AUTO_SENDS,
  GENERATE_TOOLTIP,
  GENERATE_TOOLTIP_AUTO,
  GENERATE_YOU_SEND,
  generateSuccessTitle,
} from './generate-copy';

interface Props {
  plan: DropGenPlanDetail;
  preview: DayPlanPreview | undefined;
}

export const GenerateButton: FC<Props> = ({ plan, preview }) => {
  const { hasPermission } = useAuth();
  const { generate, startCreativeFill } = useDropGeneratorMutations();
  const { success, error } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [fillCreative, setFillCreative] = useState(true);

  const blocked = !hasPermission('drop_generator.generate')
    ? 'You need the generate permission for this.'
    : plan.killSwitch
      ? 'The kill switch is on — restore it first.'
      : !plan.enabled
        ? 'Enable the plan first.'
        : !preview || preview.totals.drops === 0
          ? 'Nothing plannable today — see the Day timeline for why.'
          : null;

  const run = async () => {
    try {
      const report = await generate.mutateAsync(plan.countryKey);
      if (fillCreative)
        await startCreativeFill.mutateAsync(plan.countryKey).catch((err) => {
          error('Could not start the creative fill', getApiErrorMessage(err));
        });
      const extras = [
        report.updated > 0 ? `${report.updated} re-synced` : null,
        report.swept > 0 ? `${report.swept} stale swept` : null,
        report.skipped.length > 0 ? `${report.skipped.length} skipped` : null,
        fillCreative ? GENERATE_FILLING_NOTE : null,
      ].filter(Boolean);
      success(
        generateSuccessTitle(report.created, report.updated),
        extras.length > 0 ? extras.join(' · ') : anyGroupAutoSends(plan) ? GENERATE_AUTO_SENDS : GENERATE_YOU_SEND,
      );
      setConfirming(false);
    } catch (err) {
      error('Generation failed', getApiErrorMessage(err));
    }
  };

  const button = (
    <Button size="sm" disabled={blocked !== null} onClick={() => setConfirming(true)}>
      <Zap className="mr-1 h-3.5 w-3.5" /> {GENERATE_LABEL}
    </Button>
  );

  return (
    <>
      <Tooltip
        content={blocked ?? (anyGroupAutoSends(plan) ? GENERATE_TOOLTIP_AUTO : GENERATE_TOOLTIP)}
      >
        <span>{button}</span>
      </Tooltip>
      <ConfirmDialog
        open={confirming}
        title={GENERATE_CONFIRM_TITLE}
        description={
          preview ? (
            <span className="flex flex-col gap-3">
              <span>
                {preview.totals.drops} drops ·{' '}
                {preview.totals.sms.toLocaleString('en-US')} SMS laid out for{' '}
                {preview.runDate.slice(8, 10)}/{preview.runDate.slice(5, 7)}.{' '}
                {GENERATE_DRAFTS_ONLY}{' '}
                {anyGroupAutoSends(plan) ? `${GENERATE_AUTO_SENDS} ` : ''}
                {preview.warnings.length > 0
                  ? `${preview.warnings.length} plan warning${preview.warnings.length === 1 ? '' : 's'} stand (see the Day timeline).`
                  : 'No plan warnings.'}
              </span>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={fillCreative}
                  onChange={(e) => setFillCreative(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                {GENERATE_FILL_LABEL} (≈
                {preview.aiCalls.texts} AI call
                {preview.aiCalls.texts === 1 ? '' : 's'})
              </label>
            </span>
          ) : (
            ''
          )
        }
        confirmLabel={GENERATE_CONFIRM_LABEL}
        busy={generate.isPending}
        onConfirm={run}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
};
